import { Button } from '@/components/Button/Button';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { Ionicons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useCallback } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { H2, SizableText, YStack, getTokens } from 'tamagui';
import { appConfig as tamaguiConfig } from 'tamagui.config';

export default function ReportCreationSuccessScreen(): React.ReactNode {
  const { t } = useAppTranslation();
  const router = useRouter();
  const { status } = useLocalSearchParams<{
    status?:
      | 'draft'
      | 'queued'
      | 'csvImportPublished'
      | 'csvImportDraft';
  }>();
  const insets = useSafeAreaInsets();
  const uiTokens = getTokens();

  const handleValidate = useCallback(() => {
    router.replace('/reports');
  }, [router]);

  const iconSize = uiTokens.size.$3.val;
  const iconContainerSize = uiTokens.size.$5.val;

  let successTitle = t('reportCreation.success.title');
  let successSubtitle = t('reportCreation.success.subtitle');

  if (status === 'queued') {
    successTitle = t('reportCreation.success.queuedTitle');
    successSubtitle = t('reportCreation.success.queuedSubtitle');
  } else if (status === 'draft') {
    successTitle = t('reportCreation.success.draftTitle');
    successSubtitle = t('reportCreation.success.draftSubtitle');
  } else if (status === 'csvImportPublished') {
    successTitle = t('reportCreation.success.csvImportPublishedTitle');
    successSubtitle = t('reportCreation.success.csvImportPublishedSubtitle');
  } else if (status === 'csvImportDraft') {
    successTitle = t('reportCreation.success.csvImportDraftTitle');
    successSubtitle = t('reportCreation.success.csvImportDraftSubtitle');
  }

  return (
    <YStack
      flex={1}
      paddingHorizontal="$true"
      paddingTop={insets.top + uiTokens.space.$4.val}
      paddingBottom={Math.max(insets.bottom, uiTokens.space.$4.val)}
    >
      <YStack flex={1} alignItems="center" justifyContent="center" gap="$3">
        <YStack
          width={iconContainerSize}
          height={iconContainerSize}
          borderRadius="$circle"
          backgroundColor="$successBg"
          alignItems="center"
          justifyContent="center"
        >
          <Ionicons
            name="checkmark"
            size={iconSize}
            color={tamaguiConfig.tokens?.color?.success?.val}
          />
        </YStack>

        <YStack alignItems="center" gap="$2">
          <H2 textAlign="center">{successTitle}</H2>
          <SizableText size="$2" textAlign="center" color="$secondary75">
            {successSubtitle}
          </SizableText>
        </YStack>
      </YStack>

      <Button
        title={t('reportCreation.success.validateButton')}
        onPress={handleValidate}
        width="100%"
      />
    </YStack>
  );
}
